import * as S from './storage';
import Fetcher from './fetcher';

interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any; // Add index signature
}
export type FavoriteListType = {
    folderid: number;
    name: string;
    image?: string;
    num: number;
    shareid?: string;
    default: boolean;
};
export type UserId = number | string | undefined | null;
type CallbackF<T> = (x: T) => void;
type QueueType<T> = Array<[string, CallbackF<T>]>;

const isBrowser = typeof window !== 'undefined';
const safeWindow: Window = isBrowser ? window : {};

class ControllablePromise {
    promise: Promise<void>;

    resolve?: (value: void | PromiseLike<void>) => void;

    reject?: (reason?: unknown) => void;

    timer: number | undefined;

    constructor() {
        this.promise = new Promise((resolve, reject) => {
            this.resolve = resolve;
            this.reject = reject;
        });
    }
}

class GlobalDelayedCollector {
    namespace: string;

    handler: () => Promise<void>;

    timeout: number;

    constructor(
        namespace: string,
        handler: () => Promise<void>,
        timeout?: number,
    ) {
        this.namespace = namespace;
        this.handler = handler;
        this.timeout = timeout || 15;
    }

    private getGlobalPromise(): ControllablePromise {
        safeWindow[this.namespace] =
            safeWindow[this.namespace] || new ControllablePromise();
        return safeWindow[this.namespace];
    }

    private resetState(): void {
        delete safeWindow[this.namespace];
    }

    collectAndHandle(): Promise<void> {
        const state = this.getGlobalPromise();

        clearTimeout(state.timer);

        state.timer = window.setTimeout(() => {
            this.resetState();
            this.handler().then(state.resolve).catch(state.reject);
        }, this.timeout);

        return state.promise;
    }
}

class GlobalBus<T> {
    key: string;

    constructor(ns: string) {
        this.key = `${ns}__queue`;
        safeWindow[this.key] = safeWindow[this.key] || [];
    }

    reset(): void {
        safeWindow[this.key] = [];
    }

    getQueue(): QueueType<T> {
        return safeWindow[this.key];
    }

    // key is ItemType:ItemId (like Ad:1234)
    subscribe(key: string, func: CallbackF<T>): void {
        this.getQueue().push([key, func]);
    }

    unsubscribe(key: string, func: CallbackF<T>): void {
        safeWindow[this.key] = this.getQueue().filter(
            ([_key, _func]) => !(_key === key && _func === func),
        );
    }

    // key is ItemType:ItemId (like Ad:1234)
    matches(key: string): CallbackF<T>[] {
        return this.getQueue()
            .filter(([_key]) => _key.toString() === key.toString())
            .map(([, func]) => func);
    }

    // key is ItemType:ItemId (like Ad:1234)
    emit(key: string, state: T): void {
        this.matches(key).forEach((func) => func(state));
    }
}

export class FavoriteBrowserCache {
    namespace: string;

    fetcher: Fetcher;

    toggledStateBus: GlobalBus<boolean>;

    recountStateBus: GlobalBus<number>;

    favStorage: S.FavoriteStorage;

    getItemCollector: GlobalDelayedCollector;

    constructor(
        userId?: UserId,
        namespace?: string,
        storage?: Storage,
        ttl?: number,
        fetch?: (url: string, options?: RequestInit) => Promise<Response>,
        baseUri?: string,
    ) {
        this.namespace = namespace || '__lookupFavItem';
        this.toggledStateBus = new GlobalBus(`${this.namespace}__toggled`);
        this.recountStateBus = new GlobalBus(`${this.namespace}__recount`);
        this.favStorage = new S.FavoriteStorage(
            (key, data) => {
                this.toggledStateBus.emit(key, !!data);
            },
            userId ?? undefined,
            storage,
            ttl,
        );

        this.fetcher = new Fetcher(
            baseUri || FavoriteBrowserCache.getEnvBasedUrl(),
            fetch,
        );

        this.getItemCollector = new GlobalDelayedCollector(this.namespace, () =>
            this.fetcher
                .fetchMinimalFavoriteMap()
                .then(FavoriteBrowserCache.validateResponseOK)
                .then((res) => res.json())
                .then((j) => {
                    this.favStorage.setItems(j.items);
                })
                .catch(() => {
                    /* Ignore all errors */
                }),
        );

        this.fetchFolders = this.fetchFolders.bind(this);
        this.fetchFoldersForItem = this.fetchFoldersForItem.bind(this);
        this.addToFolder = this.addToFolder.bind(this);
        this.removeFromFolder = this.removeFromFolder.bind(this);
        this.createFolder = this.createFolder.bind(this);
        this.deleteFolder = this.deleteFolder.bind(this);
    }

    unmount(): void {
        this.toggledStateBus.reset();
        this.recountStateBus.reset();
        this.favStorage.unbind();
    }

    static getEnvBasedUrl(): string {
        const { host } = isBrowser ? window.location : { host: '' };
        if (host.includes('local.finn.no')) {
            return 'http://local.finn.no:3050/favorittliste/podium-resource/favorittlistePodlet/favorite-api';
        }
        if (host.includes('dev.finn.no')) {
            return 'https://dev.finn.no/favorittliste/podium-resource/favorittlistePodlet/favorite-api';
        }
        return 'https://www.finn.no/favorittliste/podium-resource/favorittlistePodlet/favorite-api';
    }

    getFavoriteState(
        itemType: S.ItemType,
        itemId: number | string,
        toggledFunc: (toggled: boolean) => void,
    ): () => void {
        const itemIdNumber = Number(itemId);
        this.toggledStateBus.subscribe(
            S.mkKey(itemType, itemIdNumber),
            toggledFunc,
        );
        this.isItemFavorited(itemType, itemIdNumber);
        return (): void => {
            this.toggledStateBus.unsubscribe(
                S.mkKey(itemType, itemIdNumber),
                toggledFunc,
            );
        };
    }

    subscribeToRecount(
        itemType: S.ItemType,
        itemId: number | string,
        recountFunc: (newCount: number) => void,
    ): () => void {
        const itemIdNumber = Number(itemId);
        this.recountStateBus.subscribe(
            S.mkKey(itemType, itemIdNumber),
            recountFunc,
        );
        this.fetchAndEmitNumFavs(itemType, itemIdNumber);
        return (): void => {
            this.recountStateBus.unsubscribe(
                S.mkKey(itemType, itemIdNumber),
                recountFunc,
            );
        };
    }

    isItemFavorited(
        itemType: S.ItemType,
        itemId: number | string,
    ): Promise<boolean> {
        const itemIdNumber = Number(itemId);
        if (this.favStorage.isEmpty()) {
            // This is the place that the storage gets filled up initially
            return this.getItemCollector.collectAndHandle().then(
                () =>
                    new Promise((resolve) => {
                        setTimeout(() => {
                            const favs = this.favStorage.getFolderIds(
                                itemType,
                                itemIdNumber,
                            );
                            this.toggledStateBus.emit(
                                S.mkKey(itemType, itemIdNumber),
                                favs.length > 0,
                            );
                            if (favs.length > 0) {
                                return resolve(true);
                            }
                            return resolve(false);
                        }, 0);
                    }),
            );
        }
        const favs = this.favStorage.getFolderIds(itemType, itemIdNumber); // getFromId(id);
        this.toggledStateBus.emit(
            S.mkKey(itemType, itemIdNumber),
            favs.length > 0,
        );
        if (favs.length > 0) {
            return Promise.resolve(true);
        }

        return Promise.resolve(false);
    }

    addToFolder(
        itemType: S.ItemType,
        itemId: number | string,
        folderId: number | string,
    ): Promise<Response> {
        const itemIdNumber = Number(itemId);
        const folderIdNumber = Number(folderId);
        return this.fetcher
            .addToFolder(itemType, itemIdNumber, folderIdNumber)
            .then((r) => {
                this.toggledStateBus
                    .matches(S.mkKey(itemType, itemIdNumber))
                    .forEach((cb) => cb(true));
                this.favStorage.addToFolder(
                    itemType,
                    itemIdNumber,
                    folderIdNumber,
                );
                this.fetchAndEmitNumFavs(itemType, itemIdNumber);
                return r;
            });
    }

    removeFromFolder(
        itemType: S.ItemType,
        itemId: number | string,
        folderId: number | string,
    ): Promise<Response> {
        const itemIdNumber = Number(itemId);
        const folderIdNumber = Number(folderId);
        return this.fetcher
            .removeFromFolder(itemType, itemIdNumber, folderIdNumber)
            .then((r) => {
                this.favStorage.removeFromFolder(
                    itemType,
                    itemIdNumber,
                    folderIdNumber,
                );
                this.uncheckHeartIfRemovedFromAllFolders(
                    itemType,
                    itemIdNumber,
                );
                this.fetchAndEmitNumFavs(itemType, itemIdNumber);
                return r;
            });
    }

    fetchFolders(): Promise<{ json: () => Promise<FavoriteListType[]> }> {
        // [folderid]: {name, image}
        return this.fetcher.fetchFolders();
    }

    fetchFoldersForItem(
        itemType: S.ItemType,
        itemId: number | string,
    ): Promise<S.ItemFolder> {
        const itemIdNumber = Number(itemId);
        return Promise.resolve(
            S.mkItemFolder(
                itemType,
                itemIdNumber,
                this.favStorage.getFolderIds(itemType, itemIdNumber),
            ),
        );
    }

    createFolder(name: string): Promise<Response> {
        return this.fetcher.createFolder(name);
    }

    deleteFolder(folderId: number | string): Promise<Response> {
        const folderIdNumber = Number(folderId);
        return this.fetcher.deleteFolder(folderIdNumber).then((r) => {
            this.favStorage.removeFolder(folderIdNumber);
            return r;
        });
    }

    static validateResponseOK(response: Response): Promise<Response> {
        if (response.ok) {
            return Promise.resolve(response);
        }
        return Promise.reject(response);
    }

    private uncheckHeartIfRemovedFromAllFolders(
        itemType: S.ItemType,
        itemIdNumber: number,
    ): void {
        const folders = this.favStorage.getFolderIds(itemType, itemIdNumber);
        if (folders && folders.length < 1) {
            this.toggledStateBus
                .matches(S.mkKey(itemType, itemIdNumber))
                .forEach((cb) => cb(false));
        }
    }

    private fetchAndEmitNumFavs(itemType: S.ItemType, itemId: number): void {
        this.fetchNumFavs(itemType, itemId)
            .then(FavoriteBrowserCache.validateResponseOK)
            .then((res) => this.emitFavCounter(res, itemType, itemId))
            .catch((res) => this.emitZeroCountIfNotFound(res, itemType, itemId))
            .catch(() => {
                /* Ignore all errors */
            });
    }

    private fetchNumFavs(
        itemType: S.ItemType,
        itemId: number,
    ): Promise<Response> {
        return this.fetcher.fetchNumFavs(itemType, itemId);
    }

    private emitFavCounter(
        res: Response,
        itemType: S.ItemType,
        itemId: number,
    ): Promise<void> {
        return res
            .json()
            .then((c) =>
                this.recountStateBus.emit(S.mkKey(itemType, itemId), c.counter),
            )
            .catch(() => Promise.reject(res));
    }

    private emitZeroCountIfNotFound(
        res: Response,
        itemType: S.ItemType,
        itemId: number,
    ): Promise<Response> {
        if (res.status === 404) {
            this.recountStateBus.emit(S.mkKey(itemType, itemId), 0);
            return Promise.resolve(res);
        }
        return Promise.reject(res);
    }
}

export type ItemType = S.ItemType;
export type Item = { itemType: S.ItemType; itemId: number };
export type ItemFolder = S.ItemFolder;
